import { useState, useEffect } from 'react';
import pushNotificationsSvg from '@/assets/bell-ring.svg';
import smsNotificationsSvg from '@/assets/text.svg';
import { useFirebaseFunction } from '@/hooks/useFirebaseFunction';
import { callFirebaseFunction } from '@/bridge/callFirebaseFunction';
import { requestNotificationAuthorization } from '@/bridge/requestNotificationAuthorization';
import { PushPreferences } from '../Modals/NotificationModal';

// Define types for our API calls
type GetUserPrivateInfoRequest = {
  fields?: string[];
};

type GetUserPrivateInfoResponse = {
  userInfo: {
    pushNotificationsEnabled?: boolean;
    smsNotificationsEnabled?: boolean;
    pushPreferences?: PushPreferences;
    // other fields that might be returned
  };
};

type UpdateUserPrivateInfoRequest = {
  updates: Partial<{
    pushNotificationsEnabled?: boolean;
    smsNotificationsEnabled?: boolean;
    // other fields from UserPrivateInfo
  }>;
};

type UpdateUserPrivateInfoResponse = {
  success: boolean;
};

export function NotificationSettings() {
    // State for notification settings
    const [pushNotifications, setPushNotifications] = useState(false);
    const [initialPushNotifications, setInitialPushNotifications] = useState(false);
    
    const [smsNotifications, setSMSNotifications] = useState(false);
    const [initialSMSNotifications, setInitialSMSNotifications] = useState(false);
    
    // Use hook to fetch notification settings
    const [userData, isLoading, fetchError] = useFirebaseFunction<
      GetUserPrivateInfoRequest, 
      GetUserPrivateInfoResponse
    >(
      'getUserPrivateInfo',
      (fn) => fn({ fields: ["pushNotificationsEnabled", "smsNotificationsEnabled", "pushPreferences"] }),
      []
    );
    
    // Set initial values when data is loaded
    useEffect(() => {
        if (userData && !isLoading) {
            const fetchedPushNotifications = userData.userInfo.pushNotificationsEnabled ?? false;
            const fetchedSMSNotifications = userData.userInfo.smsNotificationsEnabled ?? false;
            
            setPushNotifications(fetchedPushNotifications);
            setInitialPushNotifications(fetchedPushNotifications);
            
            setSMSNotifications(fetchedSMSNotifications);
            setInitialSMSNotifications(fetchedSMSNotifications);
        }
    }, [userData, isLoading]);
    
    // Show error toast if fetch fails
    useEffect(() => {
        if (fetchError) {
            // toast({
            //     title: "Error",
            //     description: "Failed to load notification settings. Please try again.",
            //     variant: "destructive"
            // });
        }
    }, [fetchError]);
    
    // Handle push notification toggle
    const handlePushToggle = async () => {
        const newValue = !pushNotifications;
        
        // Optimistically update UI
        setPushNotifications(newValue);

        const pushPreference = userData?.userInfo.pushPreferences ?? 'Only about free money';

        requestNotificationAuthorization(pushPreference);
        
        try {
            // Call the update function directly
            const response = await callFirebaseFunction<
              UpdateUserPrivateInfoRequest,
              UpdateUserPrivateInfoResponse
            >(
              "updateUserPrivateInfo",
              { 
                updates: { 
                  pushNotificationsEnabled: newValue 
                } 
              }
            );
            
            if (response.success) {
                // Update initial value to match current
                setInitialPushNotifications(newValue);
                // toast({
                //     title: "Success",
                //     description: `Push notifications ${newValue ? 'enabled' : 'disabled'}.`,
                // });
            }
        } catch (error) {
            // Revert to initial value if update fails
            setPushNotifications(initialPushNotifications);
            console.error("Error updating push notifications:", error);
            // toast({
            //     title: "Error",
            //     description: "Failed to update push notification settings.",
            //     variant: "destructive"
            // });
        }
    };
    
    // Handle SMS notification toggle
    const handleSMSToggle = async () => {
        const newValue = !smsNotifications;
        
        // Optimistically update UI
        setSMSNotifications(newValue);
        
        try {
            // Call the update function directly
            const response = await callFirebaseFunction<
              UpdateUserPrivateInfoRequest,
              UpdateUserPrivateInfoResponse
            >(
              "updateUserPrivateInfo",
              { 
                updates: { 
                  smsNotificationsEnabled: newValue 
                } 
              }
            );
            
            if (response.success) {
                // Update initial value to match current
                setInitialSMSNotifications(newValue);
                // toast({
                //     title: "Success",
                //     description: `SMS notifications ${newValue ? 'enabled' : 'disabled'}.`,
                // });
            }
        } catch (error) {
            // Revert to initial value if update fails
            setSMSNotifications(initialSMSNotifications);
            console.error("Error updating SMS notifications:", error);
            // toast({
            //     title: "Error",
            //     description: "Failed to update SMS notification settings.",
            //     variant: "destructive"
            // });
        }
    };
    
    return (
        <div className="mb-8">
            <h2 className="text-2xl font-bold text-blue-dark mb-6">Notification settings</h2>
            
            {isLoading ? (
                <div className="flex justify-center py-8">
                    <div className="animate-spin h-8 w-8 border-4 border-blue border-t-transparent rounded-full" />
                </div>
            ) : (
                <>
                    <div className="border border-slate-200 rounded p-4 mb-3 flex items-center justify-between">
                        <div className="flex items-center">
                            <img src={pushNotificationsSvg} className='h-6 mr-3' alt='pushNotifications'/>
                            <div>
                                <div className="font-medium text-blue-dark">Push Notifications</div>
                                <div className="text-xs text-slate">Send notifications to device</div>
                            </div>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input 
                                type="checkbox" 
                                className="sr-only peer"
                                checked={pushNotifications}
                                onChange={handlePushToggle}
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-400" />
                        </label>
                    </div>
                    
                    <div className="border border-slate-200 rounded p-4 flex items-center justify-between">
                        <div className="flex items-center">
                            <img src={smsNotificationsSvg} className='h-6 mr-3' alt='smsNotifications'/>
                            <div>
                                <div className="font-medium text-blue-dark">SMS Notifications</div>
                                <div className="text-xs text-slate">Send notifications by text</div>
                            </div>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input 
                                type="checkbox" 
                                className="sr-only peer"
                                checked={smsNotifications}
                                onChange={handleSMSToggle}
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-400" />
                        </label>
                    </div>
                    {fetchError && !fetchError?.message.includes('found') && (
                        <p className="text-red-500 text-sm mt-2.5 font-medium">{fetchError.message}</p>
                    )}
                </>
            )}
        </div>
    );
}